import NavBar from "../../layout/NavBar";
import NavBarBot from "../../layout/NavBarBot";
import { darkTheme } from "../../../theme/darkTheme";
import { useNavigate } from 'react-router-dom';
import { ThemeProvider } from "@mui/material/styles";
import {
    Box, Container, Button, Typography, Card, CardContent, Tab, Tabs,
} from "@mui/material";
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import StorageIcon from '@mui/icons-material/Storage';
import { AdminApi } from "../../../services/api";
import { useState, useCallback } from "react";
import Notification from "../../util/notifications/Notification";
import FeedbackIcon from '@mui/icons-material/Feedback';
import { darkCard } from "./components/adminConstants";
import DbExplorer from "./components/DbExplorer";
import LogsTab from "./components/LogsTab";
import UebungDialog from "./components/UebungDialog";

// ─── Hauptkomponente ──────────────────────────────────────────────────────────

const AdminPanel = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [logs, setLogs] = useState(null);
    const [logsLoaded, setLogsLoaded] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [formData, setFormData] = useState({ name: '', zielmuskel: '', kategorie: '', beschreibung: '' });
    const [formErrors, setFormErrors] = useState({});
    const [message, setMessage] = useState({ type: '', text: '' });

    const navigate = useNavigate();

    const handleTabChange = useCallback(async (_, newVal) => {
        setActiveTab(newVal);
        if (newVal === 1 && !logsLoaded) {
            try {
                const res = await AdminApi.getLogs();
                setLogs(res.data.logs);
            } catch {
                setLogs({ out: '', error: '' });
            } finally {
                setLogsLoaded(true);
            }
        }
    }, [logsLoaded]);

    const handleOpenModal = () => { setOpenModal(true); setFormErrors({}); };
    const handleCloseModal = () => {
        setOpenModal(false);
        setFormData({ name: '', zielmuskel: '', kategorie: '', beschreibung: '' });
        setFormErrors({});
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.name.trim()) errors.name = 'Name ist erforderlich';
        if (!formData.zielmuskel.trim()) errors.zielmuskel = 'Zielmuskel ist erforderlich';
        if (!formData.kategorie) errors.kategorie = 'Kategorie ist erforderlich';
        if (!formData.beschreibung.trim()) errors.beschreibung = 'Beschreibung ist erforderlich';
        return errors;
    };

    const handleSubmit = async () => {
        const errors = validateForm();
        if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }
        try {
            await AdminApi.addUebung(formData);
            setMessage({ type: 'success', text: 'Übung erfolgreich hinzugefügt!' });
        } catch {
            setMessage({ type: 'error', text: 'Fehler beim Hinzufügen der Übung.' });
        }
        handleCloseModal();
    };

    return (
        <ThemeProvider theme={darkTheme}>
            <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', display: 'flex', flexDirection: 'column', pb: 4 }}>
                <NavBar />

                {(message.type === 'error' || message.type === 'success') && (
                    <Notification type={message.type} message={message.text} onClose={() => setMessage({ type: '', text: '' })} />
                )}

                <Container maxWidth="md" sx={{ pt: { xs: 2, md: 3 }, px: { xs: 1.5, sm: 2.5 }, flexGrow: 1, pb: '72px' }}>

                    {/* Header */}
                    <Card sx={{ ...darkCard, mb: 1.5 }}>
                        <CardContent sx={{ p: '1.25rem 1.75rem !important' }}>
                            <Box display="flex" alignItems="center" gap={1.5}>
                                <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: 'rgba(59,130,246,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <AdminPanelSettingsIcon sx={{ color: '#3b82f6', fontSize: 22 }} />
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: '17px', fontWeight: 600, color: '#f1f5f9', lineHeight: 1.3 }}>Administration</Typography>
                                    <Typography sx={{ fontSize: '11px', color: '#64748b' }}>DB-Explorer &amp; System-Logs</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Übung hinzufügen */}
                    <Button
                        fullWidth variant="outlined"
                        startIcon={<FitnessCenterIcon sx={{ fontSize: 15 }} />}
                        onClick={handleOpenModal}
                        sx={{ mb: 1.5, borderRadius: '12px', textTransform: 'none', fontSize: '13px', fontWeight: 500, borderColor: 'rgba(148,163,184,.25)', color: '#94a3b8', '&:hover': { background: 'rgba(255,255,255,.04)', borderColor: 'rgba(148,163,184,.4)' } }}
                    >
                        Übung hinzufügen
                    </Button>

                    {/* Navigation zur Feedbackübersicht */}
                    <Button
                        fullWidth variant="outlined"
                        startIcon={<FeedbackIcon sx={{ fontSize: 15 }} />}
                        onClick={() => navigate('/feedback-ubersicht')}
                        sx={{ mb: 1.5, borderRadius: '12px', textTransform: 'none', fontSize: '13px', fontWeight: 500, borderColor: 'rgba(148,163,184,.25)', color: '#94a3b8', '&:hover': { background: 'rgba(255,255,255,.04)', borderColor: 'rgba(148,163,184,.4)' } }}
                    >
                        Feedback anzeigen
                    </Button>

                    {/* Haupt-Card */}
                    <Card sx={{ ...darkCard }}>
                        <Tabs
                            value={activeTab} onChange={handleTabChange}
                            sx={{
                                px: 1.75, minHeight: 40,
                                borderBottom: '1px solid rgba(255,255,255,0.06)',
                                '& .MuiTab-root': { textTransform: 'none', fontSize: '12px', fontWeight: 500, minWidth: 'auto', minHeight: 40, px: 2, color: '#64748b' },
                                '& .Mui-selected': { color: '#3b82f6 !important' },
                                '& .MuiTabs-indicator': { backgroundColor: '#3b82f6', height: '2px' },
                            }}
                        >
                            <Tab icon={<StorageIcon sx={{ fontSize: 14 }} />} iconPosition="start" label="DB-Explorer" />
                            <Tab label="Logs" />
                        </Tabs>
                        <CardContent sx={{ p: '1.25rem 1.75rem 1.5rem !important' }}>
                            {activeTab === 0 && <DbExplorer />}
                            {activeTab === 1 && <LogsTab logs={logsLoaded ? logs : null} />}
                        </CardContent>
                    </Card>

                </Container>

                {/* Modal: Übung hinzufügen */}
                <UebungDialog
                    open={openModal}
                    onClose={handleCloseModal}
                    formData={formData}
                    onInputChange={handleInputChange}
                    formErrors={formErrors}
                    onSubmit={handleSubmit}
                />

                <NavBarBot />
            </Box>
        </ThemeProvider>
    );
};

export default AdminPanel;
